import { useState, FormEvent } from "react";
import { useLang } from "@/pages/home/context/LangContext";
import { submitLead } from "@/lib/supabase/leads";

export default function Contact() {
  const { isAr, t } = useLang();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [error, setError] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem("message") as HTMLTextAreaElement)?.value ?? "";
    if (message.length > 500) return;
    // Honeypot: real visitors never see or fill this field.
    if ((form.elements.namedItem("company_website") as HTMLInputElement)?.value) { setSubmitted(true); return; }
    const field = (name: string) => (form.elements.namedItem(name) as HTMLInputElement | HTMLSelectElement | null)?.value ?? "";
    setError("");
    setSubmitting(true);
    try {
      await submitLead({
        kind: "contact",
        name: field("name"), email: field("email"), phone: field("phone"),
        property_type: field("property_type"), size: field("size"), message,
        lang: isAr ? "ar" : "en",
      });
      setSubmitted(true);
    } catch {
      setError(t(
        "We couldn't send your message. Please try again or email support@diyafa.tech.",
        "تعذّر إرسال رسالتك. حاول مرة أخرى أو راسلنا على support@diyafa.tech.",
      ));
    } finally {
      setSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: "ri-map-pin-line", en: "Muscat, Sultanate of Oman", ar: "مسقط، سلطنة عُمان" },
    { icon: "ri-mail-line", en: "support@diyafa.tech", ar: "support@diyafa.tech" },
    { icon: "ri-time-line", en: "Sun–Thu, 8am–6pm GST", ar: "الأحد–الخميس، 8ص–6م" },
  ];

  return (
    <section id="contact-form" dir={isAr ? "rtl" : "ltr"} className="py-24 bg-cream">
      <div className="max-w-7xl mx-auto px-4 md:px-8">

        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 border border-gold/20 mb-4">
            <i className="ri-customer-service-2-line text-gold text-sm"></i>
            <span className={`text-[13px] font-semibold text-[#B8892A] ${isAr ? "font-tajawal" : ""}`}>
              {t("Get in Touch", "تواصل معنا")}
            </span>
          </div>
          <h2 className={`font-bold text-3xl md:text-4xl text-ink mb-4 ${isAr ? "font-tajawal" : "font-inter"}`}>
            {t("Let's Talk About Your Property", "لنتحدث عن عقارك")}
          </h2>
          <p className={`text-base max-w-xl mx-auto text-muted ${isAr ? "font-tajawal" : ""}`}>
            {t(
              "Whether you're a small guesthouse or a large hotel chain — we'd love to show you how Diyafa can transform your operations.",
              "سواء كنت نزلاً صغيراً أو سلسلة فنادق كبيرة — يسعدنا أن نريك كيف يمكن لضيافة أن تحوّل عملياتك."
            )}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">

          {/* Left: Contact info + image */}
          <div className="lg:col-span-2 space-y-6">
            {/* Hotel image */}
            <div className="rounded-2xl overflow-hidden" style={{ height: 220 }}>
              <img
                src="https://readdy.ai/api/search-image?query=beautiful%20Omani%20hotel%20reception%20desk%20with%20traditional%20Arabic%20decor%2C%20warm%20amber%20lighting%2C%20ornate%20wooden%20details%2C%20elegant%20hospitality%20setting%2C%20professional%20photography&width=600&height=400&seq=contact-hotel&orientation=landscape"
                alt="Diyafa Hotel"
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Contact details */}
            <div className="bg-white rounded-2xl border border-line p-6 space-y-4">
              <h3 className={`font-bold text-base text-ink mb-4 ${isAr ? "font-tajawal" : ""}`}>
                {t("Contact Information", "معلومات التواصل")}
              </h3>
              {contactInfo.map((c, i) => (
                <div key={i} className={`flex items-center gap-3 ${isAr ? "flex-row-reverse" : ""}`}>
                  <div className="w-9 h-9 rounded-xl bg-gold/10 flex items-center justify-center flex-shrink-0">
                    <i className={`${c.icon} text-gold text-sm`}></i>
                  </div>
                  <span className={`text-sm text-ink-soft ${isAr ? "font-tajawal" : ""}`}>
                    {isAr ? c.ar : c.en}
                  </span>
                </div>
              ))}
            </div>

            {/* Rasad badge */}
            <div className="bg-ink rounded-2xl p-5 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center flex-shrink-0">
                <i className="ri-shield-check-line text-green-400 text-xl"></i>
              </div>
              <div className={isAr ? "text-right" : ""}>
                <p className={`font-bold text-sm text-white ${isAr ? "font-tajawal" : ""}`}>
                  {t("Rasad Certified Platform", "منصة معتمدة من رصد")}
                </p>
                <p className={`text-xs text-white/50 mt-0.5 ${isAr ? "font-tajawal" : ""}`}>
                  {t("Fully compliant with Oman's national reporting system", "متوافق تماماً مع نظام التقارير الوطني العُماني")}
                </p>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl border border-line p-8">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-double-line text-green-500 text-3xl"></i>
                  </div>
                  <h3 className={`font-bold text-lg text-ink mb-2 ${isAr ? "font-tajawal" : ""}`}>
                    {t("Message Sent!", "تم إرسال الرسالة!")}
                  </h3>
                  <p className={`text-sm text-muted ${isAr ? "font-tajawal" : ""}`}>
                    {t("Our team will get back to you within 24 hours.", "سيتواصل معك فريقنا خلال 24 ساعة.")}
                  </p>
                </div>
              ) : (
                <form id="contact-form-main" onSubmit={handleSubmit} className="space-y-5">
                  <input type="text" name="company_website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-xs font-semibold text-ink-soft mb-1.5 ${isAr ? "font-tajawal text-right" : ""}`}>
                        {t("Full Name *", "الاسم الكامل *")}
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder={t("Ahmed Al Rashidi", "أحمد الراشدي")}
                        className={`w-full px-4 py-2.5 rounded-xl border border-line-strong text-sm outline-none focus:border-gold bg-[#FAFAF8] ${isAr ? "text-right font-tajawal" : ""}`}
                      />
                    </div>
                    <div>
                      <label className={`block text-xs font-semibold text-ink-soft mb-1.5 ${isAr ? "font-tajawal text-right" : ""}`}>
                        {t("Email Address *", "البريد الإلكتروني *")}
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="ahmed@hotel.om"
                        className={`w-full px-4 py-2.5 rounded-xl border border-line-strong text-sm outline-none focus:border-gold bg-[#FAFAF8] ${isAr ? "text-right font-tajawal" : ""}`}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-xs font-semibold text-ink-soft mb-1.5 ${isAr ? "font-tajawal text-right" : ""}`}>
                        {t("Phone Number", "رقم الهاتف")}
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        placeholder="+968 9XXX XXXX"
                        className={`w-full px-4 py-2.5 rounded-xl border border-line-strong text-sm outline-none focus:border-gold bg-[#FAFAF8] ${isAr ? "text-right font-tajawal" : ""}`}
                      />
                    </div>
                    <div>
                      <label className={`block text-xs font-semibold text-ink-soft mb-1.5 ${isAr ? "font-tajawal text-right" : ""}`}>
                        {t("Property Type", "نوع العقار")}
                      </label>
                      <select
                        name="property_type"
                        className={`w-full px-4 py-2.5 rounded-xl border border-line-strong text-sm outline-none focus:border-gold bg-[#FAFAF8] ${isAr ? "text-right font-tajawal" : ""}`}
                      >
                        <option value="">{t("— Select —", "— اختر —")}</option>
                        <option value="hotel">{t("Hotel", "فندق")}</option>
                        <option value="guesthouse">{t("Guesthouse", "بيت ضيافة")}</option>
                        <option value="car_rental">{t("Car Rental", "تأجير سيارات")}</option>
                        <option value="both">{t("Hotel + Car Rental", "فندق + تأجير سيارات")}</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className={`block text-xs font-semibold text-ink-soft mb-1.5 ${isAr ? "font-tajawal text-right" : ""}`}>
                      {t("Number of Rooms / Vehicles", "عدد الغرف / المركبات")}
                    </label>
                    <select
                      name="size"
                      className={`w-full px-4 py-2.5 rounded-xl border border-line-strong text-sm outline-none focus:border-gold bg-[#FAFAF8] ${isAr ? "text-right font-tajawal" : ""}`}
                    >
                      <option value="">{t("— Select —", "— اختر —")}</option>
                      <option value="1-20">{t("1–20", "1–20")}</option>
                      <option value="21-50">{t("21–50", "21–50")}</option>
                      <option value="51-100">{t("51–100", "51–100")}</option>
                      <option value="100+">{t("100+", "100+")}</option>
                    </select>
                  </div>

                  <div>
                    <label className={`block text-xs font-semibold text-ink-soft mb-1.5 ${isAr ? "font-tajawal text-right" : ""}`}>
                      {t("Message", "الرسالة")}
                    </label>
                    <textarea
                      name="message"
                      rows={4}
                      maxLength={500}
                      onChange={e => setCharCount(e.target.value.length)}
                      placeholder={t("Tell us about your property and what you're looking for...", "أخبرنا عن عقارك وما تبحث عنه...")}
                      className={`w-full px-4 py-2.5 rounded-xl border border-line-strong text-sm outline-none focus:border-gold bg-[#FAFAF8] resize-none ${isAr ? "text-right font-tajawal" : ""}`}
                    />
                    <p className={`text-[11px] text-muted mt-1 ${isAr ? "text-left" : "text-right"}`}>{charCount}/500</p>
                  </div>

                  {error && (
                    <p role="alert" className={`text-sm text-red-600 ${isAr ? "font-tajawal" : ""}`}>{error}</p>
                  )}
                  <button
                    type="submit"
                    disabled={submitting}
                    className={`w-full py-3.5 rounded-xl font-semibold text-ink bg-gold hover:bg-gold-dark cursor-pointer transition-all disabled:opacity-60 whitespace-nowrap flex items-center justify-center gap-2 ${isAr ? "font-tajawal flex-row-reverse" : ""}`}
                  >
                    {submitting ? (
                      <><i className="ri-loader-4-line animate-spin"></i>{t("Sending...", "جارٍ الإرسال...")}</>
                    ) : (
                      <><i className="ri-send-plane-line"></i>{t("Send Message", "إرسال الرسالة")}</>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
